import { isPublicBlogImagePath } from '@/lib/site/seo/markdown-paths'
import {
  DEFAULT_LOCALE,
  ENABLED_LOCALES,
  type Locale,
  splitLocalePrefix,
} from './config'

/**
 * Which part of the app a URL belongs to.
 *
 * - `marketing`: pages under `app/[locale]` (marketing, `/explore`,
 *   `/browse/*`, auth). English is served unprefixed (`/pricing`, which the
 *   proxy rewrites to `/en/pricing`); every other locale carries its prefix
 *   (`/de/pricing`).
 * - `product`: the signed-in app and its sibling root layouts. One URL per
 *   page in every language; the locale comes from the visitor's preference.
 * - `handler`: route handlers and metadata routes outside `app/[locale]`.
 * - `localizedHandler`: a route handler English serves at the root
 *   (`/llms.txt`) and every other locale under its prefix (`/de/llms.txt`,
 *   from `app/[locale]`).
 * - `asset`: files served from `public/`.
 *
 * Every top-level segment is listed explicitly (a "has a dot" test would
 * misread the domain-share token, `payload.signature`, as a file), and
 * `tests/i18n-route-inventory.test.ts` keeps the lists in step with `app/`
 * and `public/`. An unlisted segment counts as marketing: rendered through
 * `app/[locale]`, it 404s there instead of matching the root layout with an
 * invalid locale.
 */
export type RouteArea =
  | 'marketing'
  | 'product'
  | 'handler'
  | 'localizedHandler'
  | 'asset'

export const MARKETING_SEGMENTS: ReadonlySet<string> = new Set([
  '',
  'agencies',
  'blog',
  'browse',
  'compare',
  'create-organization',
  'customers',
  'developers',
  'ecommerce',
  'email-audit',
  'enterprise',
  'explore',
  'marketers',
  'mcp',
  'pricing',
  'sign-in',
  'sign-up',
  'startups',
  'support',
  'suspended',
  'university',
])

export const PRODUCT_SEGMENTS: ReadonlySet<string> = new Set([
  'analytics',
  'assets',
  'audience',
  'automations',
  'brand',
  'brand-extraction',
  'channels',
  'chat',
  'chats',
  'domains',
  'embed',
  'emails',
  'events',
  'flows',
  'home',
  'insights',
  'integrations',
  'oauth',
  'overview',
  'plan',
  'settings',
  'setup',
  'templates',
  'test',
  'triggers',
  'unsubscribe',
  'usecases',
])

export const HANDLER_SEGMENTS: ReadonlySet<string> = new Set([
  '.well-known',
  '_vercel',
  'api',
  'continue',
  'i18n',
  'mcp.json',
  'og',
  'openapi',
  'openapi.json',
  'openapi.yaml',
  'robots.txt',
  'sitemap.xml',
  'trpc',
])

/**
 * The agent indexes: English at the root (`app/llms.txt`), every other
 * enabled locale under its prefix (`app/[locale]/llms.txt`).
 */
export const LOCALIZED_HANDLER_SEGMENTS: ReadonlySet<string> = new Set([
  'llms-full.txt',
  'llms.txt',
])

/** Top-level entries of `public/` that no route shares. */
export const ASSET_SEGMENTS: ReadonlySet<string> = new Set([
  'audio',
  'backgrounds',
  'canvas-engine-harness.html',
  'email-assets',
  'images',
  'landing',
  'logo',
  'logo.svg',
  'people',
  'rive',
  'send-mode-previews',
  'wasm',
])

const METADATA_IMAGE_SEGMENT =
  /^(?:opengraph-image|twitter-image|icon|apple-icon)(?:-[a-z0-9]{1,6})?(?:\.[a-z]+)?$/

/** A card id under a metadata image (`…/opengraph-image-<hash>/card`). */
const METADATA_IMAGE_ID = /^[a-z0-9_-]{1,32}$/i

/**
 * A metadata image route: its last segment, or the one before a
 * `generateImageMetadata` id (`ogImageMetadata`).
 */
function isMetadataImagePath(logical: string): boolean {
  const segments = logical.split('/')
  const last = segments.at(-1) ?? ''
  const parent = segments.at(-2) ?? ''
  return (
    METADATA_IMAGE_SEGMENT.test(last) ||
    (METADATA_IMAGE_ID.test(last) && METADATA_IMAGE_SEGMENT.test(parent))
  )
}

export type LocaleRoute = {
  /** The enabled locale named by the URL's first segment, if any. */
  prefixLocale: Locale | null
  /** The path without that segment: what every route check compares. */
  logical: string
  area: RouteArea
  /** An `opengraph-image` / `twitter-image` / icon route. */
  isMetadataImage: boolean
}

function areaOf(logical: string, isPrefixed: boolean): RouteArea {
  const first = logical.split('/')[1] ?? ''
  if (first.startsWith('__nextjs') || HANDLER_SEGMENTS.has(first)) {
    return 'handler'
  }
  if (LOCALIZED_HANDLER_SEGMENTS.has(first)) {
    return 'localizedHandler'
  }
  if (METADATA_IMAGE_SEGMENT.test(first)) {
    // `app/opengraph-image.tsx` answers at the root; a prefixed one is the
    // marketing tree's own (`app/[locale]/(public)/opengraph-image.tsx`).
    return isPrefixed ? 'marketing' : 'handler'
  }
  if (ASSET_SEGMENTS.has(first) || isPublicBlogImagePath(logical)) {
    return 'asset'
  }
  if (PRODUCT_SEGMENTS.has(first)) {
    return 'product'
  }
  return 'marketing'
}

export function resolveLocaleRoute(
  pathname: string,
  enabledLocales: ReadonlyArray<Locale> = ENABLED_LOCALES
): LocaleRoute {
  const { locale: prefixLocale, path: logical } = splitLocalePrefix(
    pathname,
    enabledLocales
  )
  return {
    prefixLocale,
    logical,
    area: areaOf(logical, prefixLocale !== null),
    isMetadataImage: isMetadataImagePath(logical),
  }
}

/** Areas that answer under every enabled locale's prefix. */
const LOCALIZED_AREAS: ReadonlySet<RouteArea> = new Set([
  'marketing',
  'localizedHandler',
])

/**
 * The URL a visitor sees for `logical` (a path, maybe with a query or hash)
 * in `locale`.
 */
export function localizePath(locale: Locale, logical: string): string {
  const suffixAt = logical.search(/[?#]/)
  const path = (suffixAt === -1 ? logical : logical.slice(0, suffixAt)) || '/'
  if (locale === DEFAULT_LOCALE || !LOCALIZED_AREAS.has(areaOf(path, false))) {
    return logical
  }
  const suffix = suffixAt === -1 ? '' : logical.slice(suffixAt)
  return internalLocalePath(locale, path) + suffix
}

/** The `app/[locale]` path a marketing URL is served from. */
export function internalLocalePath(locale: Locale, logical: string): string {
  return logical === '/' ? `/${locale}` : `/${locale}${logical}`
}

export type LocaleGate =
  | { kind: 'pass' }
  | { kind: 'not-found' }
  | {
      kind: 'redirect'
      status: 307 | 308
      pathname: string
      /** Store as the visitor's language choice (`LOCALE_COOKIE`). */
      rememberLocale?: Locale
    }

/**
 * What the proxy does with a URL's locale before any other routing.
 *
 * - `/en/…` answers 308 to the canonical unprefixed URL, except a metadata
 *   image: Next writes og:image as `/en/…/opengraph-image-<hash>`.
 * - A prefixed product URL answers 307 to its one URL and remembers the
 *   language, so a shared link opens in each reader's own language.
 * - A prefixed handler or asset does not exist. A localized handler does
 *   (`/de/llms.txt`), but no language choice ever redirects to it: the
 *   unprefixed file stays English.
 * - A page opened by a language switcher (`chosenLocale`, from
 *   `SET_LOCALE_PARAM`) answers 307 to the page in that language (a product
 *   page, such as the readable template mirror, keeps its one URL) and
 *   remembers the choice.
 * - An unprefixed marketing page follows an explicit language choice
 *   (`cookieLocale`). `Accept-Language` never redirects: crawlers would see a
 *   different page than the URL names. A path outside `MARKETING_SEGMENTS`
 *   (`/nope`) is not a page in any language, so it 404s where it is.
 *
 * Only GET/HEAD requests redirect: a server action POSTs to the page it was
 * rendered on. Following a cookie choice also spares Flight requests, which
 * keep the URL the router asked for; the canonical redirects (`/en/…`, a
 * prefixed product URL) apply to them too, and the router follows them.
 */
export function gateLocalePrefix(
  route: LocaleRoute,
  request: LocaleGateRequest
): LocaleGate {
  const gate = decideLocaleGate(route, request)
  // `//evil.com` names another host once Next makes a same-host Location
  // relative, so no locale redirect may point at one (`/en//evil.com`).
  // Vercel's edge collapses the slashes first; this does not rely on it.
  return gate.kind === 'redirect' && gate.pathname.startsWith('//')
    ? { kind: 'not-found' }
    : gate
}

type LocaleGateRequest = {
  method: string
  cookieLocale: Locale | null
  /** The language a switcher asked for (`SET_LOCALE_PARAM`). */
  chosenLocale?: Locale | null
  isRscRequest: boolean
}

function decideLocaleGate(
  route: LocaleRoute,
  request: LocaleGateRequest
): LocaleGate {
  const isRead = request.method === 'GET' || request.method === 'HEAD'
  const chosenLocale = request.chosenLocale ?? null
  if (
    chosenLocale !== null &&
    (route.area === 'marketing' || route.area === 'product') &&
    !route.isMetadataImage &&
    isRead &&
    !request.isRscRequest
  ) {
    return {
      kind: 'redirect',
      status: 307,
      pathname: localizePath(chosenLocale, route.logical),
      rememberLocale: chosenLocale,
    }
  }
  if (route.prefixLocale === null) {
    if (
      route.area === 'marketing' &&
      !route.isMetadataImage &&
      isRead &&
      !request.isRscRequest &&
      request.cookieLocale !== null &&
      request.cookieLocale !== DEFAULT_LOCALE &&
      MARKETING_SEGMENTS.has(route.logical.split('/')[1] ?? '')
    ) {
      return {
        kind: 'redirect',
        status: 307,
        pathname: localizePath(request.cookieLocale, route.logical),
      }
    }
    return { kind: 'pass' }
  }
  if (route.area === 'handler' || route.area === 'asset') {
    return { kind: 'not-found' }
  }
  if (route.isMetadataImage || !isRead) {
    return { kind: 'pass' }
  }
  if (route.prefixLocale === DEFAULT_LOCALE) {
    return { kind: 'redirect', status: 308, pathname: route.logical }
  }
  if (route.area === 'product') {
    return {
      kind: 'redirect',
      status: 307,
      pathname: route.logical,
      rememberLocale: route.prefixLocale,
    }
  }
  return { kind: 'pass' }
}
